import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Bell,
  ChevronDown,
  LogOut,
  Settings,
  Star,
  User,
  UserCog,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useProcessMonitoring } from "../../context/ProcessMonitoringContext";
import { useNavigate } from "react-router-dom";
import UserProfile from "./UserProfile";
import ProcessNumber from "./ProcessNumber";

export default function UserMenu() {
  const { user, logout } = useAuth();
  const { unreadCount, monitorings } = useProcessMonitoring();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const topMonitorings = useMemo(() => {
    return (monitorings || []).slice(0, 5);
  }, [monitorings]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Error logged by auth service
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      {/* User Info and Toggle */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 pl-4 border-l border-gray-300 hover:bg-gray-50 p-2 rounded-lg transition-colors"
      >
        <div className="hidden sm:block text-right">
          <p className="text-sm font-semibold text-gray-900">
            {user.name || user.username}
          </p>
          <p className="text-xs text-gray-600">{user.email || user.username}</p>
        </div>

        <div className="flex items-center space-x-2">
          <div className="relative">
            <UserProfile showDetails={false} />
            {unreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-600 ring-2 ring-white"
                title={`${unreadCount} notificação(ões) não lida(s)`}
              />
            )}
          </div>

          <ChevronDown
            className={`w-4 h-4 text-gray-600 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          {/* Profile Section */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900">
              {user.name || user.username}
            </p>
            <p className="text-xs text-gray-600">
              {user.role === "admin" || user.role === "ADMIN"
                ? "Administrador"
                : user.role === "TESTER"
                ? "Tester (somente leitura)"
                : "Usuário"}
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <User className="w-4 h-4 mr-3" />
              Meu Perfil
            </button>

            <button
              className="w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => {
                setIsOpen(false);
                navigate("/meus-monitoramentos");
              }}
            >
              <span className="flex items-center">
                <Star className="w-4 h-4 mr-3" />
                Meus monitoramentos
              </span>

              {unreadCount > 0 && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-600 text-white">
                  <Bell className="w-3 h-3" />
                  {unreadCount}
                </span>
              )}
            </button>

            {topMonitorings.length > 0 && (
              <div className="px-4 pt-2 pb-3">
                <p className="text-xs font-semibold text-gray-500 mb-2">
                  Favoritos (top {topMonitorings.length})
                </p>
                <div className="space-y-2">
                  {topMonitorings.map((m) => (
                    <div
                      key={m.id}
                      className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-3 py-2"
                    >
                      <ProcessNumber
                        processNumber={m.numberProcess}
                        copyable
                        monitorable={false}
                        className="text-gray-700"
                      />
                      <button
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => {
                          setIsOpen(false);
                          navigate("/meus-monitoramentos");
                        }}
                      >
                        ver
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ADMIN gerencia; TESTER visualiza em modo somente leitura */}
            {(user.role === "ADMIN" ||
              user.role?.toLowerCase() === "admin" ||
              user.role === "TESTER") && (
              <>
                <button
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/configuracoes");
                  }}
                >
                  <Settings className="w-4 h-4 mr-3" />
                  Configurações
                </button>

                <button
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/usuarios");
                  }}
                >
                  <UserCog className="w-4 h-4 mr-3" />
                  Usuários
                </button>
              </>
            )}

            <div className="border-t border-gray-100 my-1"></div>

            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Sair
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
